import { useParams, useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { toast } from 'react-toastify'
import useContacts, { type ContactData } from '@hooks/useContacts'
import type { Contact } from '@services/contribution/contribution'
import useOnboarding from '@/hooks/useOnboarding'

import Spinner from '@components/Spinner'
import EditContactForm from '@components/EditContactForm'
import {
  sendContactEditedEvent,
  sendContactEditErrorEvent,
  sendContactDeletedEvent,
  sendContactDeleteErrorEvent,
} from '@/services/analytics/gtm'

function findContactById(id: number, contacts: Contact[] | undefined) {
  if (!id || !contacts) {
    return null
  }
  const c = contacts?.find((contact) => contact.id === id)
  return c
}

function formatUpdateContactError(errorMessage: string): string {
  const lowerMessage = errorMessage.toLowerCase()
  if (lowerMessage.includes('404')) {
    return 'Contact could not be updated because it does not exist.'
  }
  return errorMessage || 'Sorry! We were unable to update this contact. Please try again.'
}

function formatDeleteContactError(errorMessage: string): string {
  const lowerMessage = errorMessage.toLowerCase()

  if (lowerMessage.includes('already contributed')) {
    return 'This contact has already contributed and cannot be deleted.'
  }

  if (lowerMessage.includes('parent contact')) {
    return 'This contact cannot be deleted because it is the parent contact.'
  }

  if (lowerMessage.includes('404')) {
    return 'Contact already deleted.'
  }
  return errorMessage || 'Sorry! We were unable to delete this contact. Please try again.'
}

export default function ManageContactPage() {
  const navigate = useNavigate()
  const { isOnboarding } = useOnboarding()
  const { contactId } = useParams()
  const { contacts, isLoading, updateContact, deleteContact } = useContacts()
  const contact = findContactById(Number(contactId), contacts)

  async function handleUpdateContact(data: ContactData) {
    try {
      await updateContact({
        contact: {
          id: contactId,
          name: data.name,
          alias: data.alias,
          email: data.email,
          phone: '+' + data.phone,
          language: data.language,
        },
      })
      sendContactEditedEvent({ isOnboarding: isOnboarding ?? false })
      toast.success('Contact updated successfully!')
      navigate(isOnboarding ? '../onboarding' : '../invite', { replace: true })
      return true
    } catch (error) {
      sendContactEditErrorEvent({ isOnboarding: isOnboarding ?? false })
      Sentry.logger.error('Error updating contact', { error: String(error) })
      const rawErrorMessage =
        error instanceof Error ? error.message : 'Sorry! We were unable to update this contact. Please try again.'
      const formattedMessage = formatUpdateContactError(rawErrorMessage)
      toast.error(formattedMessage, {
        autoClose: false,
      })
      return false
    }
  }

  async function handleDeleteContact() {
    try {
      if (!contactId) throw new Error('Contact Id is required to delete.')

      await deleteContact({ contactId })
      sendContactDeletedEvent({
        isOnboarding: isOnboarding ?? false,
        contactCount: (contacts?.length ?? 1) - 1,
      })
      toast.success('Contact deleted successfully!')
      navigate(isOnboarding ? '../onboarding' : '../invite', { replace: true })
      return true
    } catch (error) {
      sendContactDeleteErrorEvent({ isOnboarding: isOnboarding ?? false })
      Sentry.logger.error('Error deleting contact', { error: String(error) })
      const rawErrorMessage =
        error instanceof Error ? error.message : 'Sorry! We were unable to delete this contact. Please try again.'
      const formattedMessage = formatDeleteContactError(rawErrorMessage)
      toast.error(formattedMessage, {
        autoClose: false,
      })
      return false
    }
  }

  if (isLoading) {
    return (
      <div className='flex flex-col items-center mx-auto py-56'>
        <div className='text-xl font-serif text-white mb-4 '> Loading Contact...</div>
        <Spinner />
      </div>
    )
  }

  if (!contact) {
    return (
      <div className='flex flex-row justify-center mx-auto py-24'>
        <div className='text-xl font-serif text-white mb-4 text-center'>
          Sorry, we couldn't find a contact matching that ID.
        </div>
      </div>
    )
  }

  return (
    <>
      <title>Manage Contact - Book Blast by Books Are Fun</title>
      <EditContactForm contact={contact} onSubmit={handleUpdateContact} onDelete={handleDeleteContact} />
    </>
  )
}
